/**
 * The recording half of visit counting. The reading half is `api.visits`.
 *
 * Kept out of `api.ts` on purpose. Every call through that client attaches the
 * admin password, and this is the one request on the platform that must not
 * carry it: it is public, it needs no authority, and an organiser's password
 * should not be posted to an endpoint that does not check it.
 *
 * Everything here fails quietly. A visit that goes uncounted is a number very
 * slightly too low; a counting failure that breaks a page is a scorer standing
 * at a court unable to enter a result. The second must never happen for the
 * sake of the first.
 */
import { newVisitId, placeOf } from './visits';

const KEY = '8ball:visit';

/**
 * The id for this tab's session, in `sessionStorage` rather than
 * `localStorage`: a visit should end when the tab does. It is not a cookie, is
 * never sent to any other origin, and says nothing about who is holding the
 * phone.
 *
 * Private browsing and locked-down settings make storage throw rather than
 * return nothing, so the read is guarded. A session that cannot store its id
 * gets a fresh one per page open and is counted generously — which is the
 * wrong direction to be wrong in, so it is capped by the same means as
 * everything else: the endpoint's one-row-per-day index.
 */
function visitId(): string | null {
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = newVisitId();
    sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return null;
  }
}

/**
 * Sections already reported by this session, so a browser going back and forth
 * between the table and the fixtures does not post on every hop. The endpoint
 * would discard the repeats anyway; this saves them being sent at all.
 */
const reported = new Set<string>();

/** Records that this session opened whatever `route` points at. Never throws. */
export function recordVisit(route: string): void {
  const place = placeOf(route);
  const key = `${place.league ?? ''}/${place.section}`;
  if (reported.has(key)) return;

  const visit = visitId();
  if (!visit) return;
  reported.add(key);

  try {
    void fetch('/api/track-view', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ league: place.league, section: place.section, visit }),
      // So a visit recorded as the reader taps away still leaves the phone.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Offline, blocked by an extension, or a browser without fetch options we
    // used. None of it is the reader's problem.
  }
}
